import type { UiLanguageCode } from '@/lib/ui-languages';

/** Marché marketing LyrraOne — pays / zone de tarification. */
export type LyrraOnePublicRegion =
  | 'ma'
  | 'fr'
  | 'es'
  | 'pt'
  | 'it'
  | 'ch'
  | 'lu'
  | 'be'
  | 'dz'
  | 'tn'
  | 'tr'
  | 'intl';

export type LyrraOneCurrency = 'EUR' | 'MAD' | 'CHF' | 'DZD' | 'TND' | 'TRY';

export interface LyrraOneMarketConfig {
  countryCode: string;
  defaultLanguage: UiLanguageCode;
  currency: LyrraOneCurrency;
  locale: string;
  /** Fichier JSON pricing dédié (ex. `ma.json`) — sinon langue UI + EUR. */
  pricingBundle?: string;
  /** Fichier JSON landing dédié — réservé au Maroc pour l’instant. */
  landingBundle?: string;
}

export const LYRRAONE_PUBLIC_REGIONS: LyrraOnePublicRegion[] = [
  'ma',
  'fr',
  'es',
  'pt',
  'it',
  'ch',
  'lu',
  'be',
  'dz',
  'tn',
  'tr',
  'intl',
];

export const LYRRAONE_MARKETS: Record<LyrraOnePublicRegion, LyrraOneMarketConfig> = {
  ma: {
    countryCode: 'MA',
    defaultLanguage: 'zgh',
    currency: 'MAD',
    locale: 'fr-MA',
    pricingBundle: 'ma',
    landingBundle: 'ma',
  },
  fr: {
    countryCode: 'FR',
    defaultLanguage: 'fr',
    currency: 'EUR',
    locale: 'fr-FR',
  },
  es: {
    countryCode: 'ES',
    defaultLanguage: 'es',
    currency: 'EUR',
    locale: 'es-ES',
  },
  pt: {
    countryCode: 'PT',
    defaultLanguage: 'pt',
    currency: 'EUR',
    locale: 'pt-PT',
  },
  it: {
    countryCode: 'IT',
    defaultLanguage: 'it',
    currency: 'EUR',
    locale: 'it-IT',
  },
  ch: {
    countryCode: 'CH',
    defaultLanguage: 'fr',
    currency: 'CHF',
    locale: 'fr-CH',
    pricingBundle: 'ch',
  },
  lu: {
    countryCode: 'LU',
    defaultLanguage: 'lb',
    currency: 'EUR',
    locale: 'fr-LU',
  },
  be: {
    countryCode: 'BE',
    defaultLanguage: 'fr',
    currency: 'EUR',
    locale: 'fr-BE',
  },
  dz: {
    countryCode: 'DZ',
    defaultLanguage: 'ar',
    currency: 'DZD',
    locale: 'fr-DZ',
    pricingBundle: 'dz',
  },
  tn: {
    countryCode: 'TN',
    defaultLanguage: 'ar',
    currency: 'TND',
    locale: 'fr-TN',
    pricingBundle: 'tn',
  },
  tr: {
    countryCode: 'TR',
    defaultLanguage: 'tr',
    currency: 'TRY',
    locale: 'tr-TR',
    pricingBundle: 'tr',
  },
  intl: {
    countryCode: 'INTL',
    defaultLanguage: 'en',
    currency: 'EUR',
    locale: 'en-GB',
  },
};

const COUNTRY_TO_REGION: Record<string, LyrraOnePublicRegion> = {
  MA: 'ma',
  FR: 'fr',
  ES: 'es',
  PT: 'pt',
  IT: 'it',
  CH: 'ch',
  LU: 'lu',
  BE: 'be',
  DZ: 'dz',
  TN: 'tn',
  TR: 'tr',
};

const TIMEZONE_TO_REGION: Record<string, LyrraOnePublicRegion> = {
  'Africa/Casablanca': 'ma',
  'Europe/Paris': 'fr',
  'Europe/Madrid': 'es',
  'Atlantic/Canary': 'es',
  'Europe/Lisbon': 'pt',
  'Atlantic/Madeira': 'pt',
  'Atlantic/Azores': 'pt',
  'Europe/Rome': 'it',
  'Europe/Zurich': 'ch',
  'Europe/Luxembourg': 'lu',
  'Europe/Brussels': 'be',
  'Africa/Algiers': 'dz',
  'Africa/Tunis': 'tn',
  'Europe/Istanbul': 'tr',
  'Asia/Istanbul': 'tr',
};

const REGION_ALIASES: Record<string, LyrraOnePublicRegion> = {
  ma: 'ma',
  maroc: 'ma',
  morocco: 'ma',
  fr: 'fr',
  france: 'fr',
  es: 'es',
  espagne: 'es',
  spain: 'es',
  pt: 'pt',
  portugal: 'pt',
  portugale: 'pt',
  it: 'it',
  italie: 'it',
  italy: 'it',
  ch: 'ch',
  suisse: 'ch',
  switzerland: 'ch',
  lu: 'lu',
  luxembourg: 'lu',
  be: 'be',
  belgique: 'be',
  belgie: 'be',
  belgium: 'be',
  dz: 'dz',
  algerie: 'dz',
  algeria: 'dz',
  tn: 'tn',
  tunisie: 'tn',
  tunisia: 'tn',
  tr: 'tr',
  turquie: 'tr',
  turkey: 'tr',
  intl: 'intl',
  international: 'intl',
  internationnal: 'intl',
};

export function isLyrraOnePublicRegion(value: string): value is LyrraOnePublicRegion {
  return (LYRRAONE_PUBLIC_REGIONS as readonly string[]).includes(value);
}

export function getMarketConfig(region: LyrraOnePublicRegion): LyrraOneMarketConfig {
  return LYRRAONE_MARKETS[region];
}

export function normalizeLyrraOneRegionParam(raw: string | null | undefined): LyrraOnePublicRegion | null {
  if (!raw) return null;
  const v = raw.trim().toLowerCase();
  return REGION_ALIASES[v] ?? (isLyrraOnePublicRegion(v) ? v : null);
}

export function countryToLyrraOneRegion(country: string | null | undefined): LyrraOnePublicRegion | null {
  if (!country) return null;
  const code = country.trim().toUpperCase();
  return COUNTRY_TO_REGION[code] ?? 'intl';
}

export function detectRegionFromTimezone(): LyrraOnePublicRegion {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && TIMEZONE_TO_REGION[tz]) return TIMEZONE_TO_REGION[tz];
  } catch {
    /* ignore */
  }
  return 'intl';
}

export function getMarketPricingBundle(region: LyrraOnePublicRegion): string | null {
  return LYRRAONE_MARKETS[region].pricingBundle ?? null;
}

export function getMarketLandingBundle(region: LyrraOnePublicRegion, lang: string): string | null {
  const bundle = LYRRAONE_MARKETS[region].landingBundle;
  if (region === 'ma' && lang === 'fr') return bundle ?? 'ma';
  return null;
}
